import { useState, useEffect, useCallback } from 'react';
import { Client } from '@hiveio/dhive';

const client = new Client(['https://api.hive.blog']);

export const useUserMana = (username, autoRefreshIntervalMs = 60000) => {
  const [manaData, setManaData] = useState(null); // null means loading
  const [isRefreshingMana, setIsRefreshingMana] = useState(false);
  const [rawAccountData, setRawAccountData] = useState(null);

  const calcMana = useCallback((current, lastUpdate, max) => {
    if (!max || max <= 0) return 100;
    const elapsed = (Date.now() / 1000) - lastUpdate;
    const recovered = (elapsed * max) / 432000; // 5 days
    let mana = ((parseFloat(current) + recovered) / max) * 100;
    return Math.min(Math.max(mana, 0), 100);
  }, []);

  const calculateAndSetMana = useCallback((account, rcAccount) => {
    if (!account || !rcAccount) return;
    const maxVotingMana = parseFloat(account.post_voting_power?.split(' ')[0] || 0) * 1000000;
    const votingMana = calcMana(account.voting_manabar.current_mana, account.voting_manabar.last_update_time, maxVotingMana);
    const rcMana = calcMana(rcAccount.rc_manabar.current_mana, rcAccount.rc_manabar.last_update_time, parseFloat(rcAccount.max_rc));
    setManaData({ voting: votingMana.toFixed(2), rc: rcMana.toFixed(2) });
  }, [calcMana]);

  const fetchMana = useCallback(async (targetUsername) => {
    if (!targetUsername) return;
    
    try {
      const accounts = await client.database.getAccounts([targetUsername]);
      const rcAccounts = await client.rc.findRCAccounts([targetUsername]);
      
      if (!accounts.length || !rcAccounts.length) return;
      
      const account = accounts[0];
      const rcAccount = rcAccounts[0];
      
      setRawAccountData({ account, rcAccount });
      calculateAndSetMana(account, rcAccount);
    } catch (err) {
      console.error('Error fetching mana:', err);
    }
  }, [calculateAndSetMana]);

  // Initial fetch
  useEffect(() => {
    if (username) {
      fetchMana(username);
    }
  }, [username, fetchMana]);

  // Recalculate locally every 3 seconds to show smooth tick-up
  useEffect(() => {
    if (!rawAccountData) return;
    const interval = setInterval(() => {
      calculateAndSetMana(rawAccountData.account, rawAccountData.rcAccount);
    }, 3000);
    return () => clearInterval(interval);
  }, [rawAccountData, calculateAndSetMana]);

  // Auto-refresh from blockchain periodically
  useEffect(() => {
    if (!username || !autoRefreshIntervalMs) return;
    const interval = setInterval(() => {
      fetchMana(username);
    }, autoRefreshIntervalMs);
    return () => clearInterval(interval);
  }, [username, autoRefreshIntervalMs, fetchMana]);

  const handleRefreshMana = useCallback(async (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    if (!username || isRefreshingMana) return;
    
    setIsRefreshingMana(true);
    await fetchMana(username);
    setIsRefreshingMana(false);
  }, [username, isRefreshingMana, fetchMana]);

  return {
    manaData,
    isRefreshingMana,
    handleRefreshMana,
    fetchMana
  };
};